import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { flowReducer, initialFlowState, FlowActions } from "./flowReducer";
import { loadFlowState, saveFlowState, clearFlowState } from "./persistence";

/**
 * Flow state for one onboarding journey.
 *
 * State and dispatch are separate contexts on purpose. A screen that only
 * writes — most of them — subscribes to dispatch alone and does not re-render
 * when an unrelated answer changes elsewhere in the flow. One combined context
 * would re-render every consumer on every keystroke.
 *
 * Note on remounting: `Router.jsx` keys its `<Routes>` on `location.pathname`,
 * so the whole route tree, this provider included, remounts on every
 * navigation. That is survivable here only because state hydrates synchronously
 * from sessionStorage in the reducer's lazy initialiser — the flow picks up
 * exactly where it was, with no flash of empty state. It is also why
 * persistence is a correctness requirement in this shell rather than a
 * convenience: without it, moving to the next step would wipe the answers.
 */

const FlowStateContext = createContext(null);
const FlowDispatchContext = createContext(null);

/**
 * @param {object} props
 * @param {import('react').ReactNode} [props.children]
 * @param {import('../steps/registry').FlowState} [props.initialState]
 *   Forces a starting state instead of resuming what was persisted. For tests
 *   and for rendering a specific step in isolation.
 */
export const FlowProvider = ({ children, initialState }) => {
  const [state, dispatch] = useReducer(
    flowReducer,
    initialState,
    // Tests and stories can force a starting state; everything else resumes
    // whatever was persisted.
    (forced) => forced ?? loadFlowState(),
  );

  useEffect(() => {
    // A pristine or completed flow is not persisted, and reaching either state
    // clears what was.
    //
    // Without this, `reset()` clears storage and then immediately loses the
    // race with this effect: the dispatch produces a new state, the effect
    // fires, and the entry is written straight back. A completed signup would
    // sit in sessionStorage exactly as long as the tab stayed open, which is
    // the opposite of what the teardown was for.
    //
    // Completion is the same race with a different trigger. Done marks the flow
    // submitted on mount and clears it again when the server accepts, and the
    // two land in either order. Deciding here, in the effect that owns the
    // write, is what makes the order stop mattering.
    //
    // It also means merely visiting /onboarding and leaving writes nothing.
    if (state === initialFlowState || state.completed) {
      clearFlowState();
      return;
    }
    saveFlowState(state);
  }, [state]);

  return (
    <FlowStateContext.Provider value={state}>
      <FlowDispatchContext.Provider value={dispatch}>
        {children}
      </FlowDispatchContext.Provider>
    </FlowStateContext.Provider>
  );
};

/**
 * Both hooks throw outside a provider rather than returning a default. A screen
 * rendered outside the flow is a routing mistake, and silently handing it an
 * empty state would turn that into a confusing blank form instead of an error
 * naming the cause.
 */
export const useFlowState = () => {
  const state = useContext(FlowStateContext);
  if (state === null) {
    throw new Error("useFlowState must be used inside <FlowProvider>");
  }
  return state;
};

export const useFlowDispatch = () => {
  const dispatch = useContext(FlowDispatchContext);
  if (dispatch === null) {
    throw new Error("useFlowDispatch must be used inside <FlowProvider>");
  }
  return dispatch;
};

/**
 * Named actions, so screens dispatch `setEmail(value)` rather than assembling
 * action objects. Keeps action type strings in one place and gives each call
 * site an argument list to get wrong loudly rather than a bag of properties to
 * get wrong quietly.
 */
export const useFlowActions = () => {
  const dispatch = useFlowDispatch();

  return useMemo(
    () => ({
      setPathway: (pathway) =>
        dispatch({ type: FlowActions.SET_PATHWAY, pathway }),
      setDob: (dob) => dispatch({ type: FlowActions.SET_DOB, dob }),
      setEmail: (email) => dispatch({ type: FlowActions.SET_EMAIL, email }),
      setEmailVerified: (verified) =>
        dispatch({ type: FlowActions.SET_EMAIL_VERIFIED, verified }),
      setConsents: (consents) =>
        dispatch({ type: FlowActions.SET_CONSENTS, consents }),
      setGoals: (goals) => dispatch({ type: FlowActions.SET_GOALS, goals }),
      mergeGoalData: (data) =>
        dispatch({ type: FlowActions.MERGE_GOAL_DATA, data }),
      mergeProfile: (profile) =>
        dispatch({ type: FlowActions.MERGE_PROFILE, profile }),
      setDemographics: (demographics) =>
        dispatch({ type: FlowActions.SET_DEMOGRAPHICS, demographics }),

      /**
       * Recorded when Done opens, because arriving there sends the completion.
       * `StepGuard` reads it to refuse a way back into answers already sent.
       */
      setSubmitted: (submitted) =>
        dispatch({ type: FlowActions.SET_SUBMITTED, submitted }),

      /**
       * Recorded when the server accepts the completion. Ends persistence: the
       * effect above clears storage for a completed flow instead of writing it,
       * while the in-memory state stays intact so Done goes on rendering.
       */
      setCompleted: () => dispatch({ type: FlowActions.SET_COMPLETED }),

      /**
       * Ends the journey. Clearing storage here is belt and braces — the
       * persist effect clears it again when state reaches pristine — but it
       * makes the teardown synchronous, so nothing reads a stale entry in the
       * gap before effects run.
       */
      reset: () => {
        clearFlowState();
        dispatch({ type: FlowActions.RESET });
      },
    }),
    [dispatch],
  );
};

export { initialFlowState };
